/**
 * Calendar utility functions for generating calendar links and .ics files
 */

/**
 * Parse time string (e.g., "14:30" or "2:30 PM") to hours and minutes
 */
export const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  if (isNaN(h) || isNaN(m)) return null;
  return { h, m };
};

/**
 * Format date for Google Calendar (YYYYMMDDTHHmmssZ)
 */
export const formatForGoogle = (dt) => {
  return dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
};

/**
 * Format date for .ics file (YYYYMMDDTHHmmssZ)
 */
export const formatForICS = (dt) => {
  const year = dt.getUTCFullYear();
  const month = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  const hours = String(dt.getUTCHours()).padStart(2, "0");
  const minutes = String(dt.getUTCMinutes()).padStart(2, "0");
  const seconds = String(dt.getUTCSeconds()).padStart(2, "0");
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Generate Google Calendar URL
 */
export const generateGoogleCalendarUrl = ({ title, description, location, start, end }) => {
  const datesParam = `${formatForGoogle(start)}/${formatForGoogle(end)}`;
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    title
  )}&dates=${encodeURIComponent(datesParam)}&details=${encodeURIComponent(
    description || ""
  )}&location=${encodeURIComponent(location || "")}`;
};

/**
 * Generate Apple Calendar URL
 * Apple Calendar works best with .ics files, so we'll trigger a download
 * For web-based approach, we can use a data URL
 */
export const generateAppleCalendarUrl = ({ title, description, location, start, end }) => {
  // For Apple Calendar, we directly download the .ics file
  // This works better than trying to use webcal:// URLs
  downloadICSFile({ title, description, location, start, end });
  return null; // No URL needed since we're downloading
};

/**
 * Generate Outlook Calendar URL
 */
export const generateOutlookCalendarUrl = ({ title, description, location, start, end }) => {
  const startStr = start.toISOString();
  const endStr = end.toISOString();
  const params = new URLSearchParams({
    subject: title,
    startdt: startStr,
    enddt: endStr,
    body: description || "",
    location: location || "",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate .ics file content
 */
export const generateICSFile = ({ title, description, location, start, end }) => {
  const startStr = formatForICS(start);
  const endStr = formatForICS(end);
  const now = formatForICS(new Date());
  
  // Escape special characters for .ics format
  const escapeICS = (str) => {
    if (!str) return "";
    return str
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\n/g, "\\n");
  };

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Event Kago//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@event-kago.com`,
    `DTSTAMP:${now}`,
    `DTSTART:${startStr}`,
    `DTEND:${endStr}`,
    `SUMMARY:${escapeICS(title)}`,
    description ? `DESCRIPTION:${escapeICS(description)}` : "",
    location ? `LOCATION:${escapeICS(location)}` : "",
    "STATUS:CONFIRMED",
    "SEQUENCE:0",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter((line) => line !== "")
    .join("\r\n");

  return icsContent;
};

/**
 * Download .ics file
 */
export const downloadICSFile = ({ title, description, location, start, end }) => {
  const icsContent = generateICSFile({ title, description, location, start, end });
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Prepare event data from eventData and optional item
 */
export const prepareEventData = (eventData, item = null, locationString = "") => {
  const baseDate = new Date(eventData.date);
  if (isNaN(baseDate.getTime())) return null;

  let startTimeStr = "";
  let endTimeStr = "";

  if (item) {
    // For agenda items
    startTimeStr = item.startTime || "";
    endTimeStr = item.endTime || "";
  } else {
    // For main event
    if (eventData.time && typeof eventData.time === "string" && eventData.time.includes("-")) {
      const parts = eventData.time.split("-").map((p) => p.trim());
      startTimeStr = parts[0];
      endTimeStr = parts[1] || "";
    } else {
      startTimeStr = eventData.time || "";
      endTimeStr = eventData.endTime || "";
    }
  }

  const startParsed = parseTime(startTimeStr);
  const endParsed = parseTime(endTimeStr);

  if (!startParsed && !item) {
    // For main event, if no start time, use the date at midnight
    const start = new Date(baseDate);
    start.setHours(0, 0, 0, 0);
    let end = new Date(start);
    end.setHours(23, 59, 59, 999);
    
    return {
      title: eventData.name || eventData.title || "Event",
      description: eventData.description || "",
      location: locationString || eventData.location || eventData.venue || "",
      start,
      end,
    };
  }

  if (!startParsed) return null;

  const start = new Date(baseDate);
  start.setHours(startParsed.h, startParsed.m, 0, 0);

  let end = new Date(start);
  if (endParsed) {
    end.setHours(endParsed.h, endParsed.m, 0, 0);
  } else {
    end = new Date(start.getTime() + 60 * 60 * 1000); // Default 1 hour
  }

  const title = item
    ? item.title || item.name || `${eventData.name || eventData.title || "Event"} agenda item`
    : eventData.name || eventData.title || "Event";

  const description = item
    ? item.description || eventData.description || ""
    : eventData.description || "";

  const location = locationString || eventData.location || eventData.venue || "";

  return {
    title,
    description,
    location,
    start,
    end,
  };
};
